import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid2';
import LoadingButton from '@mui/lab/LoadingButton';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import RadioGroup from '@mui/material/RadioGroup';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import Radio from '@mui/material/Radio';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import Stack from '@mui/material/Stack';
import { Iconify } from '@mgm/common/components';
import { BASE_COLORS } from '@mgm/common/utils';
import { BaseLabel } from '@mgm/common/components/base';
import { CustomTextField } from '@mgm/common/components/form';
import Divider from '@mui/material/Divider';
import { getSelectedOrder, getVariantExchangeResult } from '../../../../store/orders/selectors';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { ProductExchangesMobile } from './ProductExchangesMobile';
import { NumericField } from '../../../../components/form';
import { TLineItem } from '../../../../types';
import { useExchangeVariantAPI } from '../../../../hooks/api/useOrders';
import { getCurrencyByName } from '../../../../store/common/selectors';

const ProductExchangesView = () => {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const { t } = useTranslation('orders');
  const exchangeResult = useAppSelector(getVariantExchangeResult);
  const getCurrency = useAppSelector(getCurrencyByName);

  const { mutateAsync: exchangeVariant, isLoading } = useExchangeVariantAPI();

  const selectedOrder = useAppSelector(getSelectedOrder);

  const steps = [t('selectLineItem'), t('exchangeDetails'), t('calculateExchanges')];

  const [variantIdToRemove, setVariantIdToRemove] = useState('');
  const [variantIdToAdd, setVariantIdToAdd] = useState('');
  const [quantityToRemove, setQuantityToRemove] = useState(0);
  const [quantityToAdd, setQuantityToAdd] = useState(0);
  const [selectedProductForExchange, setSelectedProductForExchange] = useState({} as TLineItem);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedProduct = selectedOrder.lineItems?.find((product) => product.lineItemGqlId === event.target.value);

    setSelectedProductForExchange(selectedProduct as TLineItem);
    setVariantIdToRemove(selectedProduct?.variantId as string);
    setSelectedValue(event.target.value);
  };

  const methods = useForm({
    mode: 'all',
    values: {
      variantIdToRemove: variantIdToRemove || '',
      variantIdToAdd: variantIdToAdd || '',
      quantityToRemove: quantityToRemove || 0,
      quantityToAdd: quantityToAdd || 0,
    },
  });

  const lineItems = useMemo(() => selectedOrder.lineItems?.filter((item) => item?.variantId), [selectedOrder.lineItems]);

  const handleExchangeVariant = async () => {
    const data = {
      orderId: String(selectedOrder.id),
      variantIdToRemove: String(variantIdToRemove),
      quantityToRemove,
      variantIdToAdd: String(variantIdToAdd),
      quantityToAdd,
    };

    await exchangeVariant(data);
  };

  return (
    <Grid
      container
      sx={{
        height: {
          xs: '100%',
          sm: 'calc(90dvh - var(--template-frame-height, 0px))',
        },
      }}
    >
      <Grid
        size={{ xs: 12, sm: 5, lg: 4 }}
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          backgroundColor: 'hsl(220, 35%, 97%)',
          alignItems: 'start',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            width: '100%',
            maxWidth: 600,
          }}
        >
          <RadioGroup value={selectedValue} onChange={handleChange}>
            <List sx={{
              p: 4,
            }}
            >
              <Typography pb={3} variant="subtitle1" color="primary">{t('lineItems')}</Typography>
              <Divider sx={{ borderStyle: 'dashed', mb: 3 }} />
              {lineItems?.map((value) => (
                <ListItem
                  disablePadding
                  key={value.lineItemGqlId}
                  sx={{ pb: 2 }}
                  secondaryAction={(
                    <Radio
                      value={value.lineItemGqlId}
                      checked={selectedValue === value.lineItemGqlId}
                      onChange={handleChange}
                    />
)}
                >
                  <ListItemText
                    primary={value.name}
                    primaryTypographyProps={{ variant: 'subtitle2' }}
                    secondary={`SKU: ${value.sku} | ${t('quantity')}: ${value.quantity}`}
                  />
                </ListItem>
              ))}
            </List>
          </RadioGroup>
        </Box>
      </Grid>
      <Grid
        size={{ sm: 12, md: 7, lg: 8 }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '100%',
          width: '100%',
          backgroundColor: { xs: 'transparent', sm: 'background.default' },
          alignItems: 'start',
          pt: {
            xs: 0, sm: 0, lg: 2, md: 2,
          },
          px: {
            xs: 2, sm: 10, lg: 6, md: 6,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: { sm: 'space-between', md: 'flex-end' },
            alignItems: 'center',
            width: '100%',
            maxWidth: { sm: '100%', md: 800, xs: 600 },
          }}
        >
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              flexGrow: 1,
            }}
          >
            <Stepper
              activeStep={3}
              sx={{ width: '100%', height: 40 }}
            >
              {steps.map((label) => (
                <Step
                  sx={{ ':first-child': { pl: 0 }, ':last-child': { pr: 0 } }}
                  key={label}
                >
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Box>
        <Card sx={{ display: { xs: 'flex', md: 'none' }, width: '100%', mb: 4 }}>
          <CardContent
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <Typography variant="subtitle2" gutterBottom>
                {t('selectLineItem')}
              </Typography>
            </div>
            <ProductExchangesMobile handleChange={handleChange} selectedValue={selectedValue as string} />
          </CardContent>
        </Card>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            width: '100%',
            maxWidth: { sm: '100%', md: 800, xs: 600 },
            maxHeight: '720px',
            gap: { xs: 5, md: 'none' },
          }}
        >
          <Stepper
            id="mobile-stepper"
            alternativeLabel
            activeStep={3}
            sx={{ display: { sm: 'flex', md: 'none' } }}
          >
            {steps.map((label) => (
              <Step
                sx={{
                  ':first-child': { pl: 0 },
                  ':last-child': { pr: 0 },
                  '& .MuiStepConnector-root': { top: { xs: 6, sm: 12 } },
                }}
                key={label}
              >
                <StepLabel
                  sx={{ '.MuiStepLabel-labelContainer': { maxWidth: '70px' } }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          <Divider sx={{ borderStyle: 'dashed', mt: 3 }} />
          <FormProvider {...methods}>
            <Grid width="100%" pt={4} container>
              <Grid
                size={{
                  xs: 12, sm: 12, lg: 5, md: 5,
                }}
              >
                <Typography variant="subtitle1" pb={2}>{t('currentProductForExchange')}</Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box width="100%">
                    <BaseLabel letterSpacing="-0.5px">{t('variantId')}</BaseLabel>
                    <CustomTextField
                      onChange={setVariantIdToRemove}
                      isDisplayLabel={false}
                      name="variantIdToRemove"
                      disabled
                    />
                  </Box>
                  <Box width="100%">
                    <BaseLabel letterSpacing="-0.5px">{t('quantity')}</BaseLabel>
                    <NumericField
                      name="quantityToRemove"
                      onChange={(val) => setQuantityToRemove(Number(val))}
                      borderRadius={1}
                      allowRange={[0, (selectedProductForExchange?.quantity || 0)]}
                    />
                  </Box>
                </Stack>
              </Grid>
              <Grid
                size={{
                  xs: 12, sm: 12, lg: 2, md: 2,
                }}
                justifyContent="center"
                alignItems="center"
                display="flex"
                sx={{
                  py: { xs: 2 },
                }}
              >
                <Iconify
                  icon="uil:exchange"
                  width={50}
                  sx={{
                    color: BASE_COLORS.primary,
                  }}
                />
              </Grid>
              <Grid size={{
                xs: 12, sm: 12, lg: 5, md: 5,
              }}
              >
                <Typography variant="subtitle1" pb={2}>{t('newProductInExchange')}</Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box width="100%">
                    <BaseLabel letterSpacing="-0.5px">{t('variantId')}</BaseLabel>
                    <CustomTextField
                      onChange={setVariantIdToAdd}
                      isDisplayLabel={false}
                      name="variantIdToAdd"
                    />
                  </Box>
                  <Box width="100%">
                    <BaseLabel letterSpacing="-0.5px">{t('quantity')}</BaseLabel>
                    <NumericField
                      name="quantityToAdd"
                      onChange={(val) => setQuantityToAdd(Number(val))}
                      placeholder={t('fixedPercent', { ns: 'commissions' })}
                      borderRadius={1}
                    />
                  </Box>
                </Stack>
              </Grid>
            </Grid>
            {exchangeResult?.originalSubtotal && (
              <Stack
                p={2}
                sx={{
                  backgroundColor: 'hsl(220, 35%, 97%)',
                }}
              >
                <Typography pb={1} variant="subtitle1">
                  Result:
                </Typography>
                <Typography pb={1} variant="caption">
                  {`Original order Subtotal was ${exchangeResult.originalSubtotal}${getCurrency(exchangeResult.currency).mark}.`}
                </Typography>
                <Typography pb={1} variant="caption">
                  {`Line item with variant id ${exchangeResult.removedVariant.id} had a discount amount of ${exchangeResult.removedVariant.discount}${getCurrency(exchangeResult.currency).mark} which are ${exchangeResult.removedVariant.discountPercent}%.
      Discounted unit price is ${exchangeResult.removedVariant.unitPrice}${getCurrency(exchangeResult.currency).mark}.
      After removing ${exchangeResult.removedVariant.quantity} the subtotal is ${exchangeResult.subTotalAfterRemoval}${getCurrency(exchangeResult.currency).mark}.`}
                </Typography>
                <Typography pb={1} variant="caption">
                  {`The new line item price before discount is ${exchangeResult.addedVariant.price}${getCurrency(exchangeResult.currency).mark}`}
                </Typography>
                <Typography variant="subtitle2">
                  By Amount:
                </Typography>
                <Typography pb={1} variant="caption">
                  {`After applying the same discount amount (${exchangeResult.removedVariant.discount}${getCurrency(exchangeResult.currency).mark}) per product, the new subtotal is ${exchangeResult.byAmount.subTotalAfterAdditionByAmount}${getCurrency(exchangeResult.currency).mark}
                  which means `}
                  <Typography variant="caption" color={exchangeResult.byAmount.paidBy === 'Shop' ? 'error' : 'success'}>{`the ${exchangeResult.byAmount.paidBy} needs to pay the ${exchangeResult.byAmount.paidTo} a total of ${exchangeResult.byAmount.diffFromOriginalSubtotal}${getCurrency(exchangeResult.currency).mark}`}</Typography>
                </Typography>
                <Typography variant="subtitle2">
                  By Percent:
                </Typography>
                <Typography variant="caption">
                  {`After applying the same discount percent (${exchangeResult.removedVariant.discountPercent}%) per product, the new subtotal is ${exchangeResult.byPercent.subTotalAfterAdditionByPercent}${getCurrency(exchangeResult.currency).mark}
      which means `}
                  <Typography color={exchangeResult.byAmount.paidBy === 'Shop' ? 'error' : 'success'} variant="caption">{`the ${exchangeResult.byPercent.paidBy} needs to pay the ${exchangeResult.byPercent.paidTo} a total of ${exchangeResult.byPercent.diffFromOriginalSubtotal}${getCurrency(exchangeResult.currency).mark}`}</Typography>
                </Typography>
              </Stack>
            )}
          </FormProvider>
          <Box display="flex" justifyContent="flex-end">
            <LoadingButton
              onClick={handleExchangeVariant}
              loading={isLoading}
              disabled={!selectedValue || variantIdToAdd === ''}
              variant="contained"
            >
              {t('calculate')}
            </LoadingButton>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export {
  ProductExchangesView,
};
